/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect } from 'react';
import { motion } from 'motion/react';
import {
  Package,
  Flame,
  Wind,
  Droplets,
  Sparkles,
  Shield,
  Award,
  Leaf,
  ClipboardCheck,
  CheckCircle2,
  Wrench,
  Users,
  HardHat,
  Clock,
} from 'lucide-react';

const steps = [
  {
    number: 1,
    icon: Package,
    title: 'Feedstock Loading',
    description:
      'Waste tyres or plastics are loaded into the sealed reactor.',
  },
  {
    number: 2,
    icon: Flame,
    title: 'Sealed Heating',
    description:
      'The reactor is heated to 250-400°C in an oxygen-free environment.',
  },
  {
    number: 3,
    icon: Wind,
    title: 'Oil Vapor Collection',
    description:
      'Vaporized hydrocarbons are collected through a piping system.',
  },
  {
    number: 4,
    icon: Droplets,
    title: 'Condensation',
    description:
      'Oil vapors are cooled and condensed into liquid fuel oil.',
  },
  {
    number: 5,
    icon: Sparkles,
    title: 'Output',
    description:
      'Three products: Fuel Oil (45%), Carbon Black (38%), Steel Wire (12%).',
  },
];

const certifications = [
  {
    icon: Shield,
    title: 'CE Certification',
    description: 'European safety standard compliance',
  },
  {
    icon: Award,
    title: 'ISO 9001',
    description: 'Quality management system',
  },
  {
    icon: Leaf,
    title: 'ISO 14001',
    description: 'Environmental management system',
  },
  {
    icon: ClipboardCheck,
    title: 'SGS Inspection',
    description: 'Pre-shipment quality verification',
  },
];

const envPoints = [
  'All our equipment includes emission treatment systems as standard.',
  'Dust removal, desulfurization, and exhaust purification systems ensure compliance with local environmental regulations.',
  'No wastewater discharge — closed-loop water cooling system.',
  'Noise levels within industrial zone requirements.',
];

const installPoints = [
  { icon: Wrench, text: 'Equipment foundation guidance' },
  { icon: HardHat, text: 'Professional installation team dispatch' },
  { icon: Users, text: 'Operator training program' },
  { icon: Flame, text: 'Commissioning and first-run supervision' },
  { icon: Clock, text: '12-month warranty coverage' },
  { icon: Shield, text: 'Lifetime technical support' },
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const Technology = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Banner */}
      <section className="relative bg-slate-900 py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/20 to-transparent" />

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <motion.h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            Technology
          </motion.h1>
          <motion.p
            className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.15 }}
          >
            Understanding the pyrolysis process — from feedstock to fuel.
          </motion.p>
        </div>
      </section>

      {/* How Pyrolysis Works */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-16 text-center"
            {...fadeUp}
          >
            How Pyrolysis Works
          </motion.h2>

          {/* Desktop: Horizontal timeline */}
          <div className="hidden md:flex items-start justify-between gap-4">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.number}
                  className="flex flex-col items-center text-center flex-1 relative"
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: i * 0.15 }}
                >
                  {/* Connector line */}
                  {i < steps.length - 1 && (
                    <div className="absolute top-7 left-[calc(50%+28px)] right-[calc(-50%+28px)] h-0.5 bg-slate-200 z-0" />
                  )}
                  {/* Numbered circle */}
                  <div className="relative z-10 w-14 h-14 rounded-full bg-[#00bf63] text-white flex items-center justify-center text-xl font-bold shadow-lg shadow-[#00bf63]/20">
                    {step.number}
                  </div>
                  {/* Icon */}
                  <div className="mt-4 w-12 h-12 rounded-lg bg-[#00bf63]/10 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-[#00bf63]" />
                  </div>
                  <h3 className="mt-3 text-base font-semibold text-slate-900">
                    {step.title}
                  </h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed max-w-[180px]">
                    {step.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Mobile: Vertical timeline */}
          <div className="flex flex-col gap-8 md:hidden">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.number}
                  className="flex gap-5 items-start relative"
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                >
                  {/* Vertical line */}
                  {i < steps.length - 1 && (
                    <div className="absolute left-6 top-14 bottom-0 w-0.5 bg-slate-200 -translate-y-2" />
                  )}
                  <div className="shrink-0 w-12 h-12 rounded-full bg-[#00bf63] text-white flex items-center justify-center text-lg font-bold shadow-lg shadow-[#00bf63]/20 z-10">
                    {step.number}
                  </div>
                  <div className="pt-0.5">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="w-5 h-5 text-[#00bf63]" />
                      <h3 className="text-base font-semibold text-slate-900">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Safety Standards */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-12 text-center"
            {...fadeUp}
          >
            Safety &amp; Compliance
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {certifications.map((cert, i) => {
              const Icon = cert.icon;
              return (
                <motion.div
                  key={cert.title}
                  className="rounded-xl border border-slate-200 bg-white p-6 hover:shadow-lg transition-shadow"
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                >
                  <div className="w-12 h-12 rounded-lg bg-[#00bf63]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#00bf63]" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">
                    {cert.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {cert.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          <motion.p
            className="mt-10 text-sm text-slate-500 text-center italic"
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.4 }}
          >
            Full certification documents available upon request.
          </motion.p>
        </div>
      </section>

      {/* Environmental Compliance */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-12 text-center"
            {...fadeUp}
          >
            Environmental Responsibility
          </motion.h2>

          <div className="max-w-3xl mx-auto space-y-6">
            {envPoints.map((point, i) => (
              <motion.div
                key={i}
                className="flex gap-4 items-start"
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
              >
                <CheckCircle2 className="w-6 h-6 text-[#00bf63] mt-0.5 shrink-0" />
                <p className="text-lg text-slate-600 leading-relaxed">
                  {point}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Installation & Maintenance */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 text-center"
            {...fadeUp}
          >
            Installation &amp; Maintenance
          </motion.h2>

          <motion.p
            className="text-lg text-slate-600 leading-relaxed mb-10 text-center max-w-3xl mx-auto"
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
          >
            We provide comprehensive installation support including:
          </motion.p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {installPoints.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={i}
                  className="rounded-xl border border-slate-200 bg-white p-6 hover:shadow-lg transition-shadow"
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: 0.15 + i * 0.08 }}
                >
                  <div className="w-12 h-12 rounded-lg bg-[#00bf63]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#00bf63]" />
                  </div>
                  <p className="text-base font-semibold text-slate-900">
                    {item.text}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Technology;
